import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { CallRouteV1 } from '@frontend/api-call-route';
import { i18next, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { useAlert, Text, useFormField, AutoGrowTextarea, type ResolvedFieldProps } from '@frontend/design-system';
import { trackingId as generateTrackingId } from '../../../../tracking';
import { AudioPreviewGenerator, LANGUAGE_API_MAP } from '../../../audio-picker/audio-upload-modal';
import { useGenerateTTSMutation } from '../../../audio-picker/use-audio-mutations';
import { getRelevantLocationIds } from '../../helpers/ai-receptionist-credit-utils';
import { AiReceptionistCreditNotifications } from '../ai-receptionist-credit-notifications';
import { Step } from './atoms/step';
import { AddStepPanelProps } from './types';

const PRE_TEXT = i18next.t('Hi, my name is Sam, an AI receptionist.', {
  ns: 'phone',
});
const MAX_CHARACTERS = 500;

export const getAiReceptionistDefaultParams = (message: string) => ({
  message,
  name: 'voice-assistant-greeting',
  language: LANGUAGE_API_MAP.LANGUAGE_EN_US,
  voice: 'en-US-Chirp3-HD-Leda',
});

export const WeaveAssistantStep = ({
  initialState,
  settingsTenantLocation,
  callRoute,
  callObjectsData,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT>) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();

  const [audioSrc, setAudioSrc] = useState('');
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const isInitialGenerationRef = useRef(true);
  const { getScopeName } = useAppScopeStore();

  const callRouteLocationName = getScopeName(callRoute?.locationId ?? settingsTenantLocation?.id ?? '');

  const defaultMessage =
    initialState?.callObject?.weaveAssistantExpansion?.greetingScript ||
    `Thanks for calling ${callRouteLocationName}. How can I help you today?`;

  const additionalMessageField = useFormField({
    type: 'text',
    value: defaultMessage,
  });

  // Combined message includes the default message plus any additional text
  const fullMessage = additionalMessageField.value ? `${PRE_TEXT} ${additionalMessageField.value}` : PRE_TEXT;

  const [lastGeneratedMessage, setLastGeneratedMessage] = useState(fullMessage.trim());

  const generateMutation = useGenerateTTSMutation();

  const handleGenerate = useCallback(() => {
    const isInitial = isInitialGenerationRef.current;
    isInitialGenerationRef.current = false;

    // Enable autoplay for user-initiated generations (not the initial one)
    setShouldAutoplay(!isInitial);

    generateMutation.mutate(getAiReceptionistDefaultParams(fullMessage), {
      onSuccess: (data) => {
        const audioUrl = URL.createObjectURL(data);
        setAudioSrc(audioUrl);
        setLastGeneratedMessage(fullMessage.trim());
      },
      onError: () => {
        alerts.error(t('Failed to generate audio.'));
      },
    });
  }, [fullMessage, generateMutation, alerts, t]);

  // Generate the audio preview when the component mounts so users can hear it right away.
  useEffect(() => {
    if (!audioSrc) {
      // No existing audio, generate new audio based on the default message.
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProceedClick = useCallback(() => {
    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT,
      callObject: {
        primitiveId: initialState?.callObject?.primitiveId ?? '',
        primitiveName: 'AI Receptionist',
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        weaveAssistantExpansion: {
          placeholderScript: PRE_TEXT,
          greetingScript: additionalMessageField.value.trim() ?? '',
        },
      },
    });
  }, [
    onProceedClick,
    initialState?.callObject?.instructionId,
    initialState?.callObject?.instructionSetId,
    initialState?.callObject?.primitiveId,
    additionalMessageField.value,
  ]);

  const characterCount = fullMessage.length;
  const isOverLimit = characterCount > MAX_CHARACTERS;

  // Compute credit status for AI Receptionist locations
  const relevantLocationIds = useMemo(() => {
    const phoneNumberIds = callRoute?.phoneNumberIds ?? [];
    const allPhoneNumbers = callObjectsData.allPhoneNumbers ?? [];
    const callRoutePhoneNumbers = allPhoneNumbers.filter((pn) => phoneNumberIds.includes(pn.id));
    return getRelevantLocationIds({ callRoutePhoneNumbers, allPhoneNumbers });
  }, [callRoute?.phoneNumberIds, callObjectsData.allPhoneNumbers]);

  return (
    <>
      <Step
        title={t('AI Receptionist')}
        description={t('Decide how your AI Receptionist greets your callers.')}
        primaryButtonDisabled={isOverLimit || generateMutation.isLoading}
        onPrimaryButtonClick={handleProceedClick}
        trackingIdPrefix={generateTrackingId({
          context: 'setting',
          feature: 'call-route',
          details: 'ai-receptionist-step',
        })}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(3)};
            max-width: 100%;
          `}
        >
          <AiReceptionistCreditNotifications
            locationCreditDetails={callObjectsData.locationCreditDetails}
            locationIds={relevantLocationIds}
          />

          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(2)};
            `}
          >
            <Text size='large' weight='bold'>
              {t('Greeting')}
            </Text>

            <GreetingTextarea
              fieldProps={additionalMessageField}
              label={t('Receptionist Greeting')}
              placeholder={t('Add additional greeting text...')}
              prefixText={PRE_TEXT}
              maxCharacters={MAX_CHARACTERS}
              trackingId={generateTrackingId({
                context: 'setting',
                feature: 'call-route',
                details: 'ai-receptionist-greeting-textarea',
              })}
            />
          </div>

          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(3)};
            `}
          >
            <Text size='large' weight='bold'>
              {t('Listen to Greeting')}
            </Text>

            <AudioPreviewGenerator
              buttonText={t('Generate Greeting')}
              isLoading={generateMutation.isLoading}
              isDisabled={isOverLimit || generateMutation.isLoading || fullMessage.trim() === lastGeneratedMessage}
              onGenerate={handleGenerate}
              audioSrc={audioSrc}
              generateButtonTrackingId={generateTrackingId({
                context: 'setting',
                feature: 'call-route',
                details: 'ai-receptionist-generate-greeting-btn',
              })}
              audioScrubberTrackingId={generateTrackingId({
                context: 'setting',
                feature: 'call-route',
                details: 'ai-receptionist-greeting-audio',
              })}
              autoplay={shouldAutoplay}
            />
          </div>
        </div>
      </Step>
    </>
  );
};

interface GreetingTextareaProps {
  fieldProps: ResolvedFieldProps<'text'>;
  label: string;
  placeholder: string;
  prefixText: string;
  maxCharacters?: number;
  trackingId?: string;
}

const GreetingTextarea = ({
  fieldProps,
  label,
  placeholder,
  prefixText,
  maxCharacters = MAX_CHARACTERS,
  trackingId,
}: GreetingTextareaProps) => {
  const { t } = useTranslation('phone');

  // Combined message includes the prefix text plus any additional text
  const fullMessage = fieldProps.value ? `${prefixText} ${fieldProps.value}` : prefixText;

  const characterCount = fullMessage.length;
  const charactersRemaining = maxCharacters - characterCount;
  const isOverLimit = characterCount > maxCharacters;
  const shouldShowHelper = charactersRemaining <= 100;

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border: 1px solid ${isOverLimit ? theme.colors.critical50 : theme.colors.neutral30};
          border-radius: ${theme.borderRadius.small};
          width: 100%;
          padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
          padding-top: ${theme.spacing(2.5)};
          position: relative;

          &:focus-within {
            border-color: ${isOverLimit ? theme.colors.critical50 : theme.colors.primary50};
            box-shadow: 0 0 0 1px ${isOverLimit ? theme.colors.critical50 : theme.colors.primary50};
          }
        `}
      >
        {/* The label that sits on the top-right of the border */}
        <Text
          as='label'
          size='small'
          color='subdued'
          css={css`
            padding: ${theme.spacing(0, 0.5)};
            pointer-events: none;
            position: absolute;
            top: 0;
            left: ${theme.spacing(1.5)};
            transform: translateY(-60%);

            &::before {
              content: '';
              width: 100%;
              height: calc(100% + 4px);
              background: white;
              display: block;
              position: absolute;
              top: 50%;
              left: 0;
              transform: translateY(-50%);
              z-index: -1;
            }
          `}
        >
          {label}
        </Text>

        {/* The prefix text that appears inside the text area with the greyed background */}
        <span
          css={css`
            background-color: ${theme.colors.neutral5};
            padding: ${theme.spacing(0.5)} ${theme.spacing(1)};
            border-radius: ${theme.borderRadius.small};
            font-size: ${theme.font.size.large};
            color: ${theme.font.colors.default};
            line-height: ${theme.font.lineHeight};
            font-family: ${theme.font.family};
            user-select: none;
            display: inline-block;
            width: fit-content;
            margin-bottom: ${theme.spacing(1)};
          `}
        >
          {prefixText}
        </span>
        <AutoGrowTextarea
          {...fieldProps}
          css={css`
            border: none;
            padding: 0;
            font-size: ${theme.font.size.large};
            color: ${theme.font.colors.default};
            font-family: ${theme.font.family};
            line-height: ${theme.font.lineHeight};
            outline: none;
            width: 100%;
            resize: none;

            &::placeholder {
              color: ${theme.colors.neutral40};
            }
          `}
          name='receptionist-greeting'
          placeholder={placeholder}
          aria-label={label}
          minRows={3}
          maxRows={10}
          data-trackingid={trackingId}
        />
      </div>
      {shouldShowHelper && (
        <Text
          size='small'
          color={isOverLimit ? 'error' : 'subdued'}
          css={css`
            position: relative;
            top: -8px;
          `}
        >
          {isOverLimit
            ? t('{{count}} character over limit', {
                count: Math.abs(charactersRemaining),
                count_plural: '{{count}} characters over limit',
              })
            : t('{{count}} character remaining', {
                count: charactersRemaining,
                count_plural: '{{count}} characters remaining',
              })}
        </Text>
      )}
    </>
  );
};
